import React, { memo } from 'react';
import classNames from 'classnames';
import Typography, { Variant } from '@lib/components/Typography/Typography';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import useTextCroppedTooltip from '@lib/hooks/useTextCroppedTooltip';
import styles from './CroppedText.module.scss';

export interface CroppedTextProps {
  className?: string;
  'data-test-id'?: string;
  multiLine?: boolean;
  text?: React.ReactNode;
  tooltipBody?: React.ReactNode;
  variant?: Variant;
}

/**
 * Inline text that truncates with an ellipsis and shows a tooltip ONLY when the
 * text is actually cropped. Renders via `Typography` (default `body` variant) so
 * all inline text shares the design-system styles.
 */
function CroppedText({
  className,
  'data-test-id': dataTestId,
  multiLine = false,
  text,
  tooltipBody,
  variant = 'body',
}: CroppedTextProps): React.ReactElement {
  const {
    ref: textRef,
    isTooltipDisabled,
    checkCropped,
  } = useTextCroppedTooltip<HTMLDivElement>(text);

  return (
    // `contentClassName` is applied to the wrapper element Tooltip renders
    // around its child — i.e. the component's actual root box — so a consumer's
    // `className` (e.g. flex sizing in a row) takes effect on the real layout
    // element rather than on the inner Typography.
    <Tooltip
      body={tooltipBody ?? text}
      disabled={isTooltipDisabled}
      contentClassName={classNames(styles.root, className)}
    >
      <Typography
        variant={variant}
        className={styles.text}
        data-test-id={dataTestId}
      >
        <div
          ref={textRef}
          className={classNames({
            [styles.singleLine]: !multiLine,
            [styles.multiLine]: multiLine,
          })}
          onMouseEnter={checkCropped}
        >
          {text}
        </div>
      </Typography>
    </Tooltip>
  );
}

export default memo(CroppedText);
